import { useState } from "react";
import { Clock, CheckCircle2, AlertTriangle, DollarSign, Eye } from "lucide-react";
import { Select, Button, Tag, Table, message, Drawer, Spin } from "antd";
import type { ColumnType } from "antd/es/table";
import dayjs from "dayjs";
import { ITimesheet, TTimesheetStatus } from "../../../types/api/timesheet";
import { SEMANTIC_HEX } from "../../../constants/statusColors";
import {
  useTimesheetList,
  useTimesheetStats,
  useTimesheetDetail,
  useTimesheetApprove,
  useTimesheetReject,
} from "../../../hooks/api/timesheets/hooks";

const STATUS_CFG: Record<TTimesheetStatus, { color: string; label: string }> = {
  draft: { color: "default", label: "Draft" },
  submitted: { color: "blue", label: "Submitted" },
  approved: { color: "green", label: "Approved" },
  rejected: { color: "red", label: "Rejected" },
  paid: { color: "purple", label: "Paid" },
};

const SummaryCard = ({ icon, iconBg, value, label, sub }: { icon: React.ReactNode; iconBg: string; value: string | number; label: string; sub?: string }) => (
  <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm flex items-center gap-4">
    <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg}`}>{icon}</div>
    <div>
      <div className="text-[11px] text-slate-400 uppercase tracking-wide font-medium">{label}</div>
      <div className="text-2xl font-bold text-slate-800 leading-tight">{value}</div>
      {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
  </div>
);

function TimesheetDetailDrawer({ id, onClose }: { id: number | null; onClose: () => void }) {
  const [msgApi, ctx] = message.useMessage();
  const { data: res, isLoading } = useTimesheetDetail(id);
  const ts = (res?.data as any) ?? null;

  const approve = useTimesheetApprove({
    onSuccess: () => { msgApi.success("Approved."); onClose(); },
    onError: (e) => msgApi.error(e?.response?.data?.message || "Failed"),
  });
  const reject = useTimesheetReject({
    onSuccess: () => { msgApi.success("Rejected."); onClose(); },
    onError: (e) => msgApi.error(e?.response?.data?.message || "Failed"),
  });

  return (
    <Drawer open={!!id} onClose={onClose} width={520} title="Timesheet Detail" styles={{ body: { padding: "16px 20px" } }}>
      {ctx}
      {isLoading || !ts ? (
        <div className="flex justify-center py-16"><Spin /></div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="font-semibold text-slate-800">{ts.worker_name || "—"}</span>
            <Tag color={STATUS_CFG[ts.status as TTimesheetStatus]?.color}>{STATUS_CFG[ts.status as TTimesheetStatus]?.label}</Tag>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              ["Week", `${dayjs(ts.week_start).format("DD MMM")} – ${dayjs(ts.week_end).format("DD MMM YYYY")}`],
              ["Total Hours", `${Number(ts.total_hours).toFixed(1)}h`],
              ["Overtime", `${Number(ts.overtime_hours).toFixed(1)}h`],
              ["Approved By", ts.approved_by_name || "—"],
            ].map(([l, v]) => (
              <div key={l} className="bg-slate-50 border border-slate-100 rounded-lg p-3">
                <p className="text-xs text-slate-400 mb-0.5">{l}</p>
                <p className="text-sm font-medium text-slate-700">{v}</p>
              </div>
            ))}
          </div>
          {ts.notes && (
            <div className="bg-white border border-slate-100 rounded-lg p-3">
              <p className="text-xs text-slate-400 mb-1">Notes</p>
              <p className="text-sm text-slate-700">{ts.notes}</p>
            </div>
          )}
          {/* Entries */}
          {ts.entries?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Time Entries</p>
              <div className="space-y-1">
                {ts.entries.map((e: any) => (
                  <div key={e.id} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2 text-xs">
                    <div>
                      <span className="font-medium text-slate-700">{dayjs(e.entry_date).format("ddd DD MMM")}</span>
                      <span className="text-slate-400 ml-2">{e.start_time} – {e.end_time}</span>
                    </div>
                    <div className="flex gap-2 items-center">
                      <Tag color="blue" className="text-[10px]">{e.work_type}</Tag>
                      <span className="font-semibold text-slate-700">{Number(e.hours_worked).toFixed(1)}h</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {ts.status === "submitted" && (
            <div className="flex gap-3 pt-2">
              <Button type="primary" style={{ background: SEMANTIC_HEX.success, border: "none" }} loading={approve.isPending}
                onClick={() => approve.mutate({ id: ts.id })}>
                Approve
              </Button>
              <Button danger loading={reject.isPending} onClick={() => reject.mutate({ id: ts.id })}>
                Reject
              </Button>
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
}

const TimesheetsPage = () => {
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [page, setPage] = useState(1);

  const qData = { page, limit: 20, ...(statusFilter && { status: statusFilter }) };
  const { data: listRes, isLoading, refetchWithQuery } = useTimesheetList(qData);
  const { data: statsRes } = useTimesheetStats();

  const timesheets: ITimesheet[] = (listRes?.data as any) ?? [];
  const total = Number(listRes?.extra?.total ?? 0);
  const stats = (statsRes?.data as any) ?? { pending: 0, approved: 0, total_hours: 0 };

  const columns: ColumnType<ITimesheet>[] = [
    {
      title: "Worker",
      dataIndex: "worker_name",
      key: "worker_name",
      render: (v) => (
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-full text-[11px] font-bold flex items-center justify-center flex-shrink-0" style={{ background: "rgba(1,59,190,0.1)", color: "var(--color-primary)" }}>
            {(v || "?").split(" ").map((n: string) => n[0]).join("").slice(0, 2)}
          </div>
          <span className="font-medium text-slate-800 text-sm">{v || "—"}</span>
        </div>
      ),
    },
    {
      title: "Week",
      key: "week",
      render: (_, r) => <span className="text-xs text-slate-500">{dayjs(r.week_start).format("DD MMM")} – {dayjs(r.week_end).format("DD MMM YYYY")}</span>,
      width: 160,
    },
    {
      title: "Total Hrs",
      dataIndex: "total_hours",
      key: "total_hours",
      render: (v) => <span className="font-semibold text-slate-800">{Number(v).toFixed(1)}h</span>,
      width: 90,
    },
    {
      title: "OT Hrs",
      dataIndex: "overtime_hours",
      key: "overtime_hours",
      render: (v) => <span className={Number(v) > 0 ? "font-semibold text-indigo-600" : "text-slate-400"}>{Number(v).toFixed(1)}h</span>,
      width: 80,
    },
    {
      title: "Approved By",
      dataIndex: "approved_by_name",
      key: "approved_by_name",
      render: (v) => <span className="text-xs text-slate-400">{v || "—"}</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (v: TTimesheetStatus) => <Tag color={STATUS_CFG[v]?.color}>{STATUS_CFG[v]?.label}</Tag>,
      width: 100,
    },
    {
      title: "",
      key: "actions",
      width: 100,
      render: (_, r) => (
        <Button size="small" icon={<Eye size={11} />} onClick={() => setSelectedId(r.id)}>View</Button>
      ),
    },
  ];

  return (
    <div className="p-6 min-h-screen bg-slate-50/50">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-slate-800">Timesheets</h1>
          <p className="text-slate-400 text-sm">Review and approve weekly worker timesheets</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
        <SummaryCard icon={<AlertTriangle size={18} className="text-amber-500" />} iconBg="bg-amber-50" value={stats.pending} label="Pending Approval" sub="submitted timesheets" />
        <SummaryCard icon={<CheckCircle2 size={18} className="text-emerald-500" />} iconBg="bg-emerald-50" value={stats.approved} label="Approved" sub="this period" />
        <SummaryCard icon={<Clock size={18} className="text-blue-500" />} iconBg="bg-blue-50" value={`${Number(stats.total_hours).toFixed(0)}h`} label="Total Hours" sub="all timesheets" />
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <Select
          value={statusFilter}
          onChange={(v) => { setStatusFilter(v); setPage(1); refetchWithQuery({ ...qData, status: v, page: 1 }); }}
          placeholder="All Statuses"
          allowClear
          style={{ width: 160 }}
          options={Object.entries(STATUS_CFG).map(([k, v]) => ({ value: k, label: v.label }))}
        />
      </div>

      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        <Table
          dataSource={timesheets}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={{ current: page, pageSize: 20, total, showTotal: (t) => `${t} timesheets`, showSizeChanger: false, onChange: (p) => { setPage(p); refetchWithQuery({ ...qData, page: p }); } }}
          scroll={{ x: 800 }}
        />
      </div>

      <TimesheetDetailDrawer id={selectedId} onClose={() => setSelectedId(null)} />
    </div>
  );
};

export default TimesheetsPage;
